import { EventEmitter } from 'node:events';

export interface ToolCallEvent {
  id: string | number;
  tool: string;
  args: Record<string, unknown>;
  result?: unknown;
  error?: unknown;
  durationMs: number;
}

interface PendingRequest {
  tool: string;
  args: Record<string, unknown>;
  startedAt: number;
}

type JsonRpcId = string | number;

/**
 * Inspects JSON-RPC messages flowing through the proxy.
 * Correlates tools/call requests with their responses by request ID,
 * emitting 'tool-call' events with the full tool invocation details.
 *
 * Non-tool messages pass through without side effects.
 */
export class Interceptor extends EventEmitter {
  private readonly pending = new Map<JsonRpcId, PendingRequest>();

  get pendingCount(): number {
    return this.pending.size;
  }

  /** Called for every JSON-RPC message going from agent → target MCP server. */
  onRequest(msg: Record<string, unknown>): void {
    if (msg['method'] !== 'tools/call') return;

    const id = msg['id'];
    if (id === undefined || id === null) return;

    const params = msg['params'] as Record<string, unknown> | undefined;
    if (!params) return;

    const tool = params['name'] as string;
    const args = (params['arguments'] as Record<string, unknown>) ?? {};

    this.pending.set(id as JsonRpcId, {
      tool,
      args,
      startedAt: Date.now(),
    });
  }

  /** Called for every JSON-RPC message coming from target MCP server → agent. */
  onResponse(msg: Record<string, unknown>): void {
    const id = msg['id'];
    if (id === undefined || id === null) return;

    const pendingReq = this.pending.get(id as JsonRpcId);
    if (!pendingReq) return;

    this.pending.delete(id as JsonRpcId);

    const event: ToolCallEvent = {
      id: id as JsonRpcId,
      tool: pendingReq.tool,
      args: pendingReq.args,
      durationMs: Date.now() - pendingReq.startedAt,
    };

    if (msg['result'] !== undefined) {
      event.result = msg['result'];
    }
    if (msg['error'] !== undefined) {
      event.error = msg['error'];
    }

    this.emit('tool-call', event);
  }
}
